import type { EditHappeningNowFormState } from "src/components/HappeningNow/EditHappeningNowDialog";
import {
  carouselContentHash,
  draftToSnapshotCards,
  liveToSnapshotCards,
} from "src/lib/carouselPublicationService";
import {
  platformsEqual,
  platformsFromBooleans,
} from "src/lib/happeningNowPlatforms";
import type { HappeningNowListItem } from "src/types/happeningNow";

export function liveByIdFromList(
  liveItems: HappeningNowListItem[],
): Map<string, HappeningNowListItem> {
  const m = new Map<string, HappeningNowListItem>();
  liveItems.forEach((c) => m.set(c.id, c));
  return m;
}

export function draftByIdFromList(
  draftItems: HappeningNowListItem[],
): Map<string, HappeningNowListItem> {
  const m = new Map<string, HappeningNowListItem>();
  draftItems.forEach((c) => m.set(c.id, c));
  return m;
}

export type PublishStatus = "in_sync" | "unpublished_changes" | "empty_draft";

export function computePublishStatus(
  draftItems: HappeningNowListItem[],
  liveItems: HappeningNowListItem[],
): PublishStatus {
  if (draftItems.length === 0) return "empty_draft";
  const draftHash = carouselContentHash(draftToSnapshotCards(draftItems));
  const liveHash = carouselContentHash(liveToSnapshotCards(liveItems));
  if (draftHash === liveHash) return "in_sync";
  return "unpublished_changes";
}

export function computeChangeSummary(
  draftItems: HappeningNowListItem[],
  liveItems: HappeningNowListItem[],
  publishStatus: PublishStatus,
): string | null {
  if (publishStatus !== "unpublished_changes") return null;
  const liveById = liveByIdFromList(liveItems);
  const draftIds = new Set(draftItems.map((i) => i.id));
  const liveIds = new Set(liveItems.map((i) => i.id));
  const added = draftItems.filter((i) => !liveIds.has(i.id)).length;
  const removed = liveItems.filter((i) => !draftIds.has(i.id)).length;
  let modified = 0;
  draftItems.forEach((d) => {
    const l = liveById.get(d.id);
    if (!l) return;
    if (
      d.title !== l.title ||
      (d.description ?? "") !== (l.description ?? "") ||
      d.imageUrl !== l.imageUrl ||
      d.targetLink !== l.targetLink ||
      (d.startsAt?.toMillis() ?? null) !== (l.startsAt?.toMillis() ?? null) ||
      d.expiresAt.toMillis() !== l.expiresAt.toMillis() ||
      d.active !== l.active ||
      d.order !== l.order ||
      !platformsEqual(d.platforms, l.platforms)
    ) {
      modified += 1;
    }
  });
  const parts: string[] = [];
  if (added) parts.push(`${added} new`);
  if (modified) parts.push(`${modified} edited`);
  if (removed) parts.push(`${removed} removed`);
  return parts.length > 0 ? parts.join(" · ") : "draft differs from live";
}

export function resolveEditingItem(
  edit: { id: string } | null,
  draftItems: HappeningNowListItem[],
  liveById: Map<string, HappeningNowListItem>,
): HappeningNowListItem | null {
  if (!edit) return null;
  return (
    draftItems.find((i) => i.id === edit.id) ?? liveById.get(edit.id) ?? null
  );
}

/** Compare edit form to original live row (same expiry/start string encoding). */
export function editFromLiveIsUnchanged(
  edit: EditHappeningNowFormState,
  orig: HappeningNowListItem,
  origExpiresLocal: string,
  origStartsLocal: string,
  imageFile: File | null,
): boolean {
  const editPlatforms = platformsFromBooleans(
    edit.platformAndroid,
    edit.platformIos,
  );
  if (!editPlatforms) return false;
  return (
    !imageFile &&
    edit.title === orig.title &&
    (edit.description || "") === (orig.description ?? "") &&
    edit.targetLink === orig.targetLink &&
    (edit.startsAtLocal || "") === origStartsLocal &&
    edit.expiresAtLocal === origExpiresLocal &&
    edit.active === orig.active &&
    platformsEqual(editPlatforms, orig.platforms)
  );
}

export function liveCardsRemovedOnPublish(
  draftItems: HappeningNowListItem[],
  liveItems: HappeningNowListItem[],
): HappeningNowListItem[] {
  const draftIds = new Set(draftItems.map((i) => i.id));
  return liveItems.filter((l) => !draftIds.has(l.id));
}
